import React, { useState } from 'react';
import { bindActionCreators } from 'redux';
import { toAssetsExtUrl } from 'utils/assetsExtUrl';
import { connect } from 'react-redux';
import classname from 'classnames';
import {getUpcomingAndLiveSessions} from 'actions/AgendaActions';
import { agendaSpeakerSelector, speakersSelector } from 'selectors/AgendaSelectors';
import { siteConfSelector } from 'selectors/SiteConfSelectors';
import { eventIdSelector, eventTimezoneSelector } from 'selectors/EventSelectors';
import { sessionsByDaySelector } from '../../../selectors/AgendaSelectors';
import { OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';

import {
    getTimeFromSessionStart,
    getSessionDuration,
    useInterval,
    isSessionDurationOver,
    isSessionStartsInMorethan,
    isSessionEnded,
    loadVirtualSessionPage
} from './sessionBroadcastUtils';
import JoinLiveSessionButton from './JoinLiveSessionButton';
import {
    L_WIDTH,
    M_WIDTH,
    S_WIDTH,
    XL_WIDTH,
    XS_WIDTH,
    XXS_WIDTH,
    loginClickOrigin,
} from '../../../constants/Enums';
import BZCarousel from '../../../../../web-common/bizzabo-components/components/BZCarousel';
import { setScrollToSessionBroadcastAction } from '../../../actions/AgendaActions';

const emptyPersonIcon = '/images/agenda/SpeakerIcon.png';

const UPDATE_LIVE_SESSIONS_INTERVAL = 30 * 1000;

const GET_LIVE_AND_UPCOMING_SESSIONS_INTERVAL = 30 * 60 * 1000;


const NextArrow = (props) => {
    const {className, style, onClick} = props;
    return (
        <div
            className={`custom-color color-1 ${className}`}
            style={{...style, display: 'block'}}
            onClick={onClick}
        ><i className="fa icon-AgendaAtt_arrow carousel-arrow"/></div>
    );
};

const PrevArrow = (props) => {
    const {className, style, onClick} = props;
    return (
        <div
            className={`custom-color color-1 ${className}`}
            style={{...style, display: 'block'}}
            onClick={onClick}
        ><i className="fa icon-AgendaAtt_arrow mirror carousel-arrow"/></div>
    );
};

const sliderSettings = (fixedNumOfItems) => ({
    responsive: [
        { breakpoint: XXS_WIDTH, settings: { slidesToShow: 1.1 } },
        { breakpoint: XS_WIDTH, settings: { slidesToShow: 1.2 } },
        { breakpoint: S_WIDTH, settings: { slidesToShow: 1.5 } },
        { breakpoint: M_WIDTH, settings: { slidesToShow: fixedNumOfItems || 2.3 } },
        { breakpoint: L_WIDTH, settings: { slidesToShow: fixedNumOfItems || 2.5 } },
        { breakpoint: XL_WIDTH, settings: { slidesToShow: fixedNumOfItems || 3.5 } },
    ],
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />
});

const formatFullName = ({ prefix = '', firstname = '', lastname = '', suffix = '' }) =>
    `${prefix} ${firstname} ${lastname} ${suffix}`.replace(/\s+/g, ' ').trim();

const OneSpeaker = ({ speaker = {} }) => {
    const { firstname = '', lastname = '', thumbnailUrl, title = '', company = '', prefix = '', suffix = '' } = speaker;
    const fullName = formatFullName({ prefix, firstname, lastname, suffix });
    const role = company ? `${title}, ${company}` : `${title}${company}`;
    const avatarClass = classname('live-speaker-avatar margin-right-xs', {'live-avatar-border': !thumbnailUrl });
    return <div className='live-session-speaker bz-flex-row'>
        <div className='avatar-container margin-right-sm'>
            <img className={avatarClass} src={toAssetsExtUrl(thumbnailUrl) || emptyPersonIcon} />
        </div>
        <div className='speaker-details-container custom-font'>
            {fullName &&<div className={'live-speaker-name'}>{fullName}</div>}
            {role && <div className={'live-speaker-role'}>{role}</div>}
        </div>
    </div>;
};

const MultipleSpeakers = ({ speakers = [], sessionId }) => {
    const amountOfSpeakers = speakers.length;
    const moreThan6Speakers = amountOfSpeakers > 6;
    const speakerLine = moreThan6Speakers ? speakers.slice(0, 5) : speakers;
    const moreSpeakers = moreThan6Speakers ? speakers.slice(5) : [];
    const moreSpeakersContainer = classname('more-speakers bz-flex-row align-items-center', {'hide': amountOfSpeakers <= 6});
    const moreSpeakersCls = classname('color-1', {'hide': amountOfSpeakers <= 6});
    const MoreSpeakersPopover = <Popover id={`more-speaker-popover-${sessionId}`}>
        <div className='bz-flex-col more-speaker-popover'>
            {moreSpeakers.map(speaker => <OneSpeaker key={speaker?.id} speaker={speaker}/>)}
        </div>
    </Popover>;
    return <div className='bz-flex-row'>
        {speakerLine.map(speaker => {
            const { firstname = '', lastname = '', thumbnailUrl, title = '', company = '', prefix = '', suffix = ''} = speaker || {};
            const position = company ? `${title},` : `${title}`;
            const fullName = formatFullName({ prefix, firstname, lastname, suffix });
            const avatarClass = classname('live-speaker-avatar margin-right-sm', {'live-avatar-border': !thumbnailUrl });
            const popover = <Popover id={speaker?.id}>
                <div className='speaker-details-popover custom-font'>
                    {fullName && <div className='live-speaker-name'>{fullName}</div>}
                    {title && <div className='live-speaker-role'>{position}</div>}
                    {company && <div className='live-speaker-role'>{company}</div>}
                </div>
            </Popover>;
            return <div className='live-session-speaker' key={speaker?.id}>
                <OverlayTrigger delay={500} placement='bottom' overlay={popover}>
                    <img className={avatarClass} src={toAssetsExtUrl(thumbnailUrl) || emptyPersonIcon} />
                </OverlayTrigger>
            </div>;
        })}
        <OverlayTrigger trigger='click' rootClose={true} placement='bottom' overlay={MoreSpeakersPopover}>
            <div className={moreSpeakersContainer}>
                <span className={moreSpeakersCls}>{moreSpeakers.length} More</span>
            </div>
        </OverlayTrigger>
    </div>;
};

const LiveSessionBox = (props) => {
    const {
        session,
        eventTimeZone,
        clientNavigationAction,
        event,
        setScrollToSessionBroadcastAction,
        session: {backgroundColor},
    } = props;
    const { title, speakers = [], startDate, startMinute, id, endDate, endMinute } = session || {};
    const [timeFromSessionStart, setTimeFromSessionStart] = useState('00:00:00');
    const [sessionIsOver, setSessionIsOver] = useState(false);
    const sessionDuration = getSessionDuration({startDate, startMinute, endDate, endMinute, eventTimeZone});
    useInterval(() =>{
        const updatedTimeFromSessionStart = getTimeFromSessionStart({startDate, startMinute, eventTimeZone});
        const isSessionIsOver = isSessionDurationOver({startDate, startMinute, endDate, endMinute, eventTimeZone});
        setSessionIsOver(isSessionIsOver);
        if (isSessionIsOver){
            setTimeFromSessionStart(sessionDuration);
        } else {
            setTimeFromSessionStart(updatedTimeFromSessionStart);
        }
    }, 1000);
    const isMoreThanOneSpeaker = speakers.length > 1;
    const isOneSpeaker = speakers.length === 1;

    const onSessionTitleClick = async(e) => {
        e.preventDefault();
        await loadVirtualSessionPage(session.id, event);
    };
    return <div className='live-session-box-container'>
        <div className='live-session-box bz-flex-col' style={{background: `${backgroundColor}`}}>
            <div className='bz-flex-row justify-content-start session-box-header'>
                {! sessionIsOver &&
                    <span className='live-label'>
                        <span className='circle-live-now'></span> LIVE
                    </span>
                }
                <span className='font-size-12 session-timer'>{timeFromSessionStart}</span><span className='font-size-12 session-duration'>&nbsp;/ {sessionDuration}</span>
            </div>
            <div className='session-name custom-font'>
                <a onClick={onSessionTitleClick}>{title}</a>
            </div>
            <div className='live-session-speakers'>
                { isMoreThanOneSpeaker && <MultipleSpeakers speakers={speakers} sessionId={session?.id} /> }
                { isOneSpeaker && <OneSpeaker speaker={speakers[0]}/> }
            </div>
            <JoinLiveSessionButton
                isDisabled={sessionIsOver}
                setScrollToSessionBroadcastAction={setScrollToSessionBroadcastAction}
                analyticData={{
                    clickOrigin: loginClickOrigin.agenda.liveBanner,
                }}
                {...props}
            />
        </div>
    </div>;

};

const mapStateToProps = (state) => ({
    getSpeaker: (speakerId) => agendaSpeakerSelector(state, { eventId: eventIdSelector(state), speakerId }),
    sessions: sessionsByDaySelector(state),
    eventTimeZone: eventTimezoneSelector(state),
    siteConf: siteConfSelector(state)
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    getUpcomingAndLiveSessions,
    setScrollToSessionBroadcastAction
}, dispatch);

const updateSessions = ({
    liveSessions = [],
    upcomingSessions,
    eventTimeZone,
    setUpcomingSessions,
    setLiveSessions,
    updateVirtualSessionExist,
    prevVirtualSessionExist
}) => {
    let updatedLiveSessionsObject = {};
    liveSessions.forEach(session => {
        const isSessionOver = isSessionEnded(session, eventTimeZone);
        if (!isSessionOver){
            updatedLiveSessionsObject[session.id] = session;
        }
    });

    let updatedUpcomingSessions = [];
    upcomingSessions.forEach(session => {
        const sessionHasNotStartedYet = isSessionStartsInMorethan(session, 0, eventTimeZone);
        if (sessionHasNotStartedYet) {
            updatedUpcomingSessions.push(session);
        } else {
            const isSessionOver = isSessionEnded(session, eventTimeZone);
            if (!isSessionOver) {
                updatedLiveSessionsObject[session.id] = session;
            }
        }
    }
    );

    const updatedLiveSessions = Object.values(updatedLiveSessionsObject);

    setUpcomingSessions(updatedUpcomingSessions);
    setLiveSessions(updatedLiveSessions);
    const virtualSessionExist = updatedLiveSessions.length > 0;
    if (prevVirtualSessionExist !== virtualSessionExist) {
        updateVirtualSessionExist(virtualSessionExist);
    }
};

const LiveNowBanner = ({
    getUpcomingAndLiveSessions,
    event,
    me,
    eventUrls,
    showEmailLogin,
    isFeatureSessionVirtualElementEnabled,
    setCurrentNewrowVideocast,
    setWebcastingSessionCreds,
    setEmbeddedVideoDetails,
    setVirtualSessionDetails,
    eventTimeZone,
    toggleShowLiveNowBanner,
    clientNavigationAction,
    showLiveNowBanner,
    currentSessionId,
    virtualSessionExist: prevVirtualSessionExist,
    updateVirtualSessionExist,
    setScrollToSessionBroadcastAction,
    setRecordUrl,
    showRegisterAndLoginModal,
    siteConf,
    setLoginV2OffsetTop,
    ticketsSelectionData,
}) => {
    const [liveSessions, setLiveSessions] = useState([]);
    const [upcomingSessions, setUpcomingSessions] = useState([]);
    const isInSessionPage = !!currentSessionId;
    useInterval(async() =>{
        try {
            const upcomingAndLiveSessions = await getUpcomingAndLiveSessions();

            updateSessions({
                upcomingSessions: upcomingAndLiveSessions,
                eventTimeZone,
                setUpcomingSessions,
                setLiveSessions,
                updateVirtualSessionExist,
                prevVirtualSessionExist
            });
        } catch (e) {
            //
        }
    }, GET_LIVE_AND_UPCOMING_SESSIONS_INTERVAL);
    useInterval(() =>{
        updateSessions({
            liveSessions,
            upcomingSessions,
            eventTimeZone,
            setUpcomingSessions,
            setLiveSessions,
            updateVirtualSessionExist,
            prevVirtualSessionExist
        });
    }, UPDATE_LIVE_SESSIONS_INTERVAL, false);
    const updatedLiveSession = liveSessions?.filter(session => session.id !== currentSessionId);
    const numberOfLiveSessions = updatedLiveSession?.length;
    const sliderSettingsParam = numberOfLiveSessions < 3 ? numberOfLiveSessions : null;
    const bannerCls = classname('agenda-live-banner bz-flex-row', {'agenda-live-banner-session-page': isInSessionPage});
    const closeButtonCls = classname('open-banner-button', {'open-banner-button-session-page': isInSessionPage});
    return (
        <>
        { showLiveNowBanner && numberOfLiveSessions > 0 &&
         <div className={bannerCls}>
             <div className='bz-flex-row live-sessions-row'>
                 <BZCarousel settings={sliderSettings(sliderSettingsParam)}>
                     {updatedLiveSession && updatedLiveSession.map(session =>
                         <LiveSessionBox
                             key={session?.id}
                             session={session}
                             clientNavigationAction={clientNavigationAction}
                             setScrollToSessionBroadcastAction={setScrollToSessionBroadcastAction}
                             setLoginV2OffsetTop={setLoginV2OffsetTop}
                             {...{
                                 eventTimeZone,
                                 event,
                                 me,
                                 eventUrls,
                                 showEmailLogin,
                                 isFeatureSessionVirtualElementEnabled,
                                 setCurrentNewrowVideocast,
                                 setWebcastingSessionCreds,
                                 setEmbeddedVideoDetails,
                                 setRecordUrl,
                                 showRegisterAndLoginModal,
                                 siteConf,
                                 setVirtualSessionDetails,
                                 ticketsSelectionData
                             }}
                         />
                     )}
                 </BZCarousel>
             </div>
             <div className='live-banner-closer-container' onClick={() => toggleShowLiveNowBanner(false)}>
                 <div className='live-banner-closer color-1-background' >
                     <i className="fa icon-close"/>
                 </div>
             </div>
             <div className='live-banner-fold-container' onClick={() => toggleShowLiveNowBanner(false)}>
                 <div className='live-banner-fold color-1-background' >
                     <i className="fa icon-AgendaAtt_arrow fold-icon"/>
                 </div>
             </div>
         </div>
        }
            {!showLiveNowBanner && numberOfLiveSessions > 0 && <div className={closeButtonCls} onClick={() => toggleShowLiveNowBanner(true)}>
                <span className='live-label font-size-14 margin-right-xs'>
                    <span className='circle-live-now'></span>
                    LIVE
                </span>
                <span className='caret margin-left-xs'></span>

            </div>}
            </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(LiveNowBanner);
